import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Link, Paper, Stack, Typography, useTheme } from '@mui/material';

import { useGetCurrentUser, useGetUserById } from '@/api/generated';
import PhoneInput from '@/components/fields/PhoneInput';
import StandardPopper from '@/components/StandardPopper/StandardPopper';
import typography from '@/theme/typography';

const PhoneUpdateInput = () => {
  const { t } = useTranslation('users');
  const { spacing, palette } = useTheme();
  const { userId } = useParams<{ userId: string }>();

  const { data: currentUser } = useGetCurrentUser();
  const { data: userData } = useGetUserById(userId!);

  const isCurrentUser = currentUser?.id === userId;

  const mailtoLink = useMemo(() => {
    const supportEmail = t('users.phoneChange.supportEmail');
    const subject = encodeURIComponent(t('users.phoneChange.subject'));
    const body = encodeURIComponent(t('users.phoneChange.body', { currentPhone: userData?.phone || '' }));

    return `mailto:${supportEmail}?subject=${subject}&body=${body}`;
  }, [t, userData?.phone]);

  return (
    <Stack alignItems="start" gap={0.5}>
      <PhoneInput
        withFormControl
        name="phone"
        value={userData?.phone}
        label={t('users.fields.phone')}
        outerSx={{ p: 0, width: '100%' }}
        disabled
        markAsRequired
      />
      {isCurrentUser && (
        <StandardPopper
          caretStartPosition="10%"
          content={
            <Paper
              sx={{
                mt: 1,
                p: spacing(0.75, 1.5),
                position: 'relative',
                maxWidth: '240px',
                '.StandardPopper-root &': {
                  bgcolor: palette.primary[100]
                }
              }}
            >
              <Typography sx={typography.body3}>{t('users.phoneChange.requestToChangeTooltip')}</Typography>
            </Paper>
          }
          getSx={() => ({
            zIndex: 10000
          })}
          getTriggerSx={() => ({
            display: 'inline-flex',
            alignSelf: 'flex-start',
            height: 'auto',
            width: 'auto',
            justifyContent: 'flex-start'
          })}
          hoverable
          placement="bottom-start"
        >
          <Link
            href={mailtoLink}
            sx={{
              alignSelf: 'flex-start',
              minWidth: 'auto',
              px: 0,
              py: 0,
              height: 'auto',
              lineHeight: spacing(2),
              ...typography.body3,
              fontWeight: typography.fontWeightMedium,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            {t('users.actions.requestToChange')}
          </Link>
        </StandardPopper>
      )}
    </Stack>
  );
};

export default PhoneUpdateInput;
